"use client";

import { useState, useTransition } from "react";
import { useUser } from "@clerk/nextjs";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { votePost } from "@/lib/server-actions/restoration/votes";

type VoteValue = "up" | "down" | null;

interface PostVoteButtonsProps {
  postId: number;
  upCount: number;
  downCount: number;
  myVote: VoteValue;
  onChange?: (next: { up: number; down: number; myVote: VoteValue }) => void;
  className?: string;
  size?: "sm" | "md" | "lg";
}

/**
 * JCG-1570: 복원 게시물 투표 버튼.
 * - 로그인 사용자만 투표 가능 (비로그인 시 toast)
 * - 자기 글에도 투표 가능 (서버 RPC가 차단하지 않음)
 * - 같은 버튼 재클릭 → 취소, 반대 버튼 클릭 → 교체
 * - 낙관적 업데이트 후 서버 결과로 동기화
 */
export default function PostVoteButtons({
  postId,
  upCount,
  downCount,
  myVote,
  onChange,
  className,
  size = "md",
}: PostVoteButtonsProps) {
  const { isLoaded, isSignedIn } = useUser();
  const [up, setUp] = useState(upCount);
  const [down, setDown] = useState(downCount);
  const [current, setCurrent] = useState<VoteValue>(myVote);
  const [isPending, startTransition] = useTransition();

  const submit = (target: "up" | "down") => {
    if (!isLoaded || isPending) return;
    if (!isSignedIn) {
      toast.error("로그인 후 투표할 수 있습니다.");
      return;
    }

    const prevSnapshot = { up, down, current };
    const nextVote: "up" | "down" | "none" =
      current === target ? "none" : target;

    let nextUp = up;
    let nextDown = down;
    if (current === "up") nextUp = Math.max(0, nextUp - 1);
    if (current === "down") nextDown = Math.max(0, nextDown - 1);
    if (nextVote === "up") nextUp += 1;
    if (nextVote === "down") nextDown += 1;

    setUp(nextUp);
    setDown(nextDown);
    setCurrent(nextVote === "none" ? null : nextVote);

    startTransition(async () => {
      const result = await votePost(postId, nextVote);
      if (!result.success) {
        setUp(prevSnapshot.up);
        setDown(prevSnapshot.down);
        setCurrent(prevSnapshot.current);
        toast.error(result.error || "투표에 실패했습니다.");
        return;
      }
      setUp(result.up);
      setDown(result.down);
      setCurrent(result.myVote);
      onChange?.({
        up: result.up,
        down: result.down,
        myVote: result.myVote,
      });
    });
  };

  const buttonBase =
    "inline-flex items-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 dark:focus-visible:ring-neutral-100 dark:focus-visible:ring-offset-neutral-900 disabled:opacity-60 disabled:cursor-not-allowed";
  const sizing =
    size === "sm"
      ? "gap-1 px-2 py-1 text-xs"
      : size === "md"
        ? "gap-1 px-3 py-1.5 text-sm"
        : "gap-2 px-5 py-3 text-base sm:text-lg font-semibold";
  const iconSize =
    size === "sm"
      ? "w-3.5 h-3.5"
      : size === "md"
        ? "w-4 h-4"
        : "w-5 h-5 sm:w-6 sm:h-6";

  const containerGap = size === "lg" ? "gap-3 sm:gap-4" : "gap-2";
  const isDisabled = !isLoaded || isPending;

  return (
    <div className={cn("flex items-center", containerGap, className)}>
      <button
        type="button"
        aria-label="찐이다"
        aria-pressed={current === "up"}
        disabled={isDisabled}
        onClick={() => submit("up")}
        className={cn(
          buttonBase,
          sizing,
          current === "up"
            ? "border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-950/40 dark:text-emerald-300"
            : "border-gray-200 text-gray-600 hover:border-emerald-400 hover:text-emerald-700 dark:border-neutral-700 dark:text-neutral-300 dark:hover:border-emerald-500 dark:hover:text-emerald-300"
        )}
      >
        <ThumbsUp className={iconSize} />
        {size === "lg" && <span className="hidden sm:inline">찐이다</span>}
        <span className="font-medium">{up}</span>
      </button>
      <button
        type="button"
        aria-label="아닌 것 같다"
        aria-pressed={current === "down"}
        disabled={isDisabled}
        onClick={() => submit("down")}
        className={cn(
          buttonBase,
          sizing,
          current === "down"
            ? "border-rose-500 bg-rose-50 text-rose-700 dark:border-rose-400 dark:bg-rose-950/40 dark:text-rose-300"
            : "border-gray-200 text-gray-600 hover:border-rose-400 hover:text-rose-700 dark:border-neutral-700 dark:text-neutral-300 dark:hover:border-rose-500 dark:hover:text-rose-300"
        )}
      >
        <ThumbsDown className={iconSize} />
        {size === "lg" && <span className="hidden sm:inline">아닌 것 같다</span>}
        <span className="font-medium">{down}</span>
      </button>
    </div>
  );
}
